import { apiDelete, apiGet, apiPatch, apiPost, apiPut, authorizedApiRequest } from '../../api/oauth/helpers.ts';

const BASE = '/api/sop-synthesizer';

export type SopStatus =
  | 'generating'
  | 'draft'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'published'
  | 'archived';
export type SopRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type SopSectionKind =
  | 'purpose'
  | 'roles'
  | 'systems'
  | 'schedule'
  | 'procedure'
  | 'evidence'
  | 'exceptions'
  | 'open_items'
  | 'caveats'
  | 'other';

/** How far along a video's media preparation is, as the tenant sees it. Only "ready" videos can start a SOP. */
export type SopMediaStatus = 'ready' | 'pending' | 'processing' | 'failed' | 'expired';

export interface SopVideoOption {
  id: string;
  title: string;
  original_filename: string;
  duration_seconds?: number;
  size_bytes: number;
  media_status: SopMediaStatus;
  uploaded_at?: string;
  processed_at?: string;
}

export interface SopRun {
  id: string;
  sop_id: string;
  status: SopRunStatus;
  stage?: string;
  progress: number;
  message?: string;
  error_message?: string;
  model?: string;
  input_tokens: number;
  output_tokens: number;
  instructions?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
}

export type SopTextMarkupType = 'highlight' | 'comment';

export type SopImageMarkupType =
  | 'image_rect'
  | 'image_ellipse'
  | 'image_arrow'
  | 'image_pen'
  | 'image_text'
  | 'image_callout'
  | 'image_pin';

export type SopMarkupType = SopTextMarkupType | SopImageMarkupType;

export interface SopMarkupAnnotation {
  id: string;
  section_id: string;
  section_heading?: string;
  type: SopMarkupType;
  /** Required for text highlight/comment; optional for image marks. */
  quote?: string;
  comment?: string;
  color?: string;
  created_at?: string;
  /** Image markup (frame:<id> in section body). */
  frame_id?: string;
  /** Normalized 0–1 box/ellipse/pin. */
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  /** Normalized arrow endpoints. */
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  /** Normalized freehand path [[x,y], ...]. */
  points?: number[][];
  /** Label for image_text / image_callout. */
  text?: string;
  callout_number?: number;
}

export interface Sop {
  id: string;
  organization_id: string;
  video_id: string;
  title: string;
  description: string;
  status: SopStatus;
  row_version: number;
  markup_annotations?: SopMarkupAnnotation[];
  created_at?: string;
  updated_at?: string;
}

export interface SopSummary extends Sop {
  video_title?: string;
  latest_run?: SopRun;
}

export interface SopSection {
  id: string;
  sop_id: string;
  position: number;
  kind: SopSectionKind;
  heading: string;
  body: string;
  locked: boolean;
  row_version: number;
}

export interface SopCitation {
  id: string;
  section_id: string;
  marker: string;
  start_seconds: number;
  end_seconds: number;
  quote: string;
  frame_artifact_id?: string;
  frame_url?: string;
}

export interface SopDetail {
  sop: Sop;
  sections: SopSection[];
  citations: SopCitation[];
  /** artifact id -> signed URL, for screenshots referenced as frame:<id> in section bodies */
  frames: Record<string, string>;
  run?: SopRun;
  video: { id: string; title?: string; duration_seconds?: number; url?: string };
}

export async function fetchVideos(): Promise<SopVideoOption[]> {
  const data = await apiGet(`${BASE}/videos`);
  return Array.isArray(data?.videos) ? data.videos : [];
}

export async function fetchSops(): Promise<SopSummary[]> {
  const data = await apiGet(`${BASE}/sops`);
  return Array.isArray(data?.sops) ? data.sops : [];
}

export async function fetchSop(id: string): Promise<SopDetail> {
  return apiGet(`${BASE}/sops/${id}`);
}

/** Optional platform context from Process Charts (nested cycle → process → roles/systems). */
export interface CreateSopPlatformContext {
  cycles: Array<{
    id: string;
    cycle_id: string;
    name?: string;
    processes: Array<{
      process_id: string;
      process_business_id: string;
      name?: string;
      flowchart_version_id: string;
      flowchart_version_label?: string;
      actors: Array<{ id: string; name: string }>;
      systems: Array<{ id: string; name: string }>;
    }>;
  }>;
  /** Unique union across all selected process versions (convenience for AI). */
  actors: Array<{ id: string; name: string }>;
  systems: Array<{ id: string; name: string }>;
}

export interface CreateSopBody {
  video_id: string;
  title: string;
  description?: string;
  instructions?: string;
  /** Start writing the draft right away (default true). False saves an empty draft. */
  generate?: boolean;
  /** Optional; omitted when the user does not pick platform context. */
  platform_context?: CreateSopPlatformContext;
}

export async function createSop(body: CreateSopBody): Promise<{ sop: Sop; run: SopRun | null }> {
  return apiPost(`${BASE}/sops`, body);
}

export type SopWorkflowStatus = 'draft' | 'in_review' | 'changes_requested' | 'approved' | 'published';

export async function updateSop(
  id: string,
  body: { title?: string; description?: string; status?: SopWorkflowStatus },
): Promise<Sop> {
  return (await apiPatch(`${BASE}/sops/${id}`, body)).sop;
}

/** Create a section. Server sets kind to "other". */
export async function createSopSection(
  sopId: string,
  body: { heading: string; body?: string },
): Promise<SopSection> {
  return (await apiPost(`${BASE}/sops/${sopId}/sections`, body)).section;
}

/** Upload a section screenshot; returns artifact id + signed URL (insert as frame:<id> in body). */
export async function uploadSopScreenshot(
  sopId: string,
  file: File,
): Promise<{ id: string; url: string; filename: string }> {
  const form = new FormData();
  form.append('file', file);
  return authorizedApiRequest({
    method: 'POST',
    url: `${BASE}/sops/${sopId}/screenshots`,
    data: form,
    // Let the browser set multipart boundary (do not force application/json).
    headers: { Accept: 'application/json' },
  });
}

/** Patch one section's markdown body; optional heading updates sop_sections.heading. */
export async function updateSopSection(
  sopId: string,
  sectionId: string,
  body: { body: string; heading?: string },
): Promise<SopSection> {
  return (await apiPatch(`${BASE}/sops/${sopId}/sections/${sectionId}`, body)).section;
}

/** Reorder sections; section_ids must be all live section ids in the new order (positions 1..N). */
export async function reorderSopSections(sopId: string, sectionIds: string[]): Promise<void> {
  await apiPut(`${BASE}/sops/${sopId}/sections/sequence`, { section_ids: sectionIds });
}

export async function regenerateSop(id: string, instructions?: string): Promise<{ sop: Sop; run: SopRun }> {
  return apiPost(`${BASE}/sops/${id}/generate`, { instructions: instructions ?? '' });
}

export async function saveSopMarkup(
  id: string,
  annotations: SopMarkupAnnotation[],
): Promise<SopMarkupAnnotation[]> {
  const data = await apiPut(`${BASE}/sops/${id}/markup`, { annotations });
  return Array.isArray(data?.markup_annotations) ? data.markup_annotations : [];
}

export async function cancelSopGeneration(id: string): Promise<void> {
  await apiPost(`${BASE}/sops/${id}/cancel`, {});
}

export async function deleteSop(id: string): Promise<void> {
  await apiDelete(`${BASE}/sops/${id}`);
}

/** A SOP can be edited or deleted until it enters review; after that changes go through the review flow. */
export function isSopEditable(status: SopStatus): boolean {
  return status === 'generating' || status === 'draft' || status === 'changes_requested';
}

export const LOCKED_SOP_MESSAGE = "SOPs that are in review, approved, or published can't be deleted.";
